import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'

import { Spinner } from '@droidmap/frontend-ui'

interface AppConfig {
  ENV: string
  COGNITO_CONFIG: {
    UserPoolId: string
    ClientId: string
  }
  /**
   * Base URL of the cognito-service HTTP API. Used by the unauthenticated
   * confirm-signup flow to resolve an email address to a username before
   * ConfirmSignUp. Optional so local-dev config.json (which has no backend)
   * still parses. See #2120.
   */
  COGNITO_SERVICE_URL?: string
  RUM_CONFIG?: {
    applicationId: string
    applicationVersion?: string
    region: string
    identityPoolId: string
    /** @deprecated Not used - RUM uses enhanced auth flow without guestRoleArn */
    guestRoleArn?: string
    sessionSampleRate?: number
    telemetries?: string[]
    allowCookies?: boolean
    enableXRay?: boolean
  }
}

const ConfigContext = createContext<AppConfig | null>(null)

export const useConfig = () => {
  const context = useContext(ConfigContext)
  if (context === null) {
    throw new Error('useConfig must be used within a ConfigProvider')
  }
  return context
}

interface ConfigProviderProps {
  children: ReactNode
}

const fetchConfig = async (): Promise<AppConfig> => {
  const response = await fetch('/config.json')
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  const config = await response.json()
  console.debug('User pool config', {
    env: config.ENV,
  })
  return config
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<AppConfig | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const loadedConfig = await fetchConfig()
        setConfig(loadedConfig)
      } catch (error) {
        console.error('Failed to load configuration:', error)
      } finally {
        setLoading(false)
      }
    }
    loadConfig()
  }, [])

  if (loading) {
    return (
      <div>
        <Spinner size="page" className="text-accent" />
      </div>
    )
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  )
}
